import { useEffect } from "@wordpress/element";
import { useSettings } from "../../context/SettingsContext";
import {
    Toggle,
    SettingRow,
    SectionCard,
    ButtonGroup,
    NumberInput,
    ColorInput,
    SelectInput,
    TextInput,
    InfoBox,
} from "../ui/index";

const THUMB_TRIGGERS = [
    { value: "click", label: "Click" },
    { value: "hover", label: "Hover" },
];

const THUMB_SLIDE_TYPES = [
    { value: "row", label: "Slide by Row" },
    { value: "item", label: "Slide by Item" },
];

const THUMB_POSITIONS = [
    { value: "bottom", label: "Bottom" },
    { value: "top", label: "Top" },
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
];

const THUMB_LAYOUTS = [
    { value: "slider", label: "Slider" },
    { value: "grid", label: "Grid" },
];

const THUMB_ALIGNMENTS_HORIZONTAL = [
    { value: "left", label: "Left" },
    { value: "center", label: "Center" },
    { value: "right", label: "Right" },
];

const THUMB_ALIGNMENTS_VERTICAL = [
    { value: "left", label: "Top" },
    { value: "center", label: "Center" },
    { value: "right", label: "Bottom" },
];

const IMAGE_SIZES = [
    { value: "thumbnail", label: "Thumbnail (150×150)" },
    { value: "medium", label: "Medium (300×300)" },
    { value: "medium_large", label: "Medium Large (768px)" },
    { value: "large", label: "Large (1024px)" },
    { value: "woocommerce_thumbnail", label: "WC Thumbnail" },
    { value: "woocommerce_single", label: "WC Single" },
    { value: "woocommerce_gallery_thumbnail", label: "WC Gallery Thumbnail" },
    { value: "full", label: "Full (original)" },
];

const HEIGHT_MODES = [
    { value: "lock", label: "Lock" },
    { value: "adaptive", label: "Adaptive" },
    { value: "fixed", label: "Fixed" },
];

const IMG_FITS = [
    { value: "contain", label: "Contain" },
    { value: "cover", label: "Cover" },
];

const TRANSITIONS = [
    { value: "fade", label: "Fade" },
    { value: "slide", label: "Slide" },
    { value: "zoom", label: "Zoom" },
    { value: "none", label: "None" },
];

const SLIDE_DIRECTIONS = [
    { value: "horizontal", label: "Horizontal" },
    { value: "vertical", label: "Vertical" },
];

const PAGINATION_TYPES = [
    { value: "dots", label: "Dots" },
    { value: "progressbar", label: "Progress Bar" },
    { value: "count", label: "Count" },
];

const PAGINATION_POSITIONS = [
    { value: "bottom", label: "Bottom" },
    { value: "top", label: "Top" },
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
];

const GALLERY_TYPES = [
    { value: "thumbnail", label: "Thumbnail Gallery" },
    { value: "hierarchy-grid", label: "Hierarchy Grid" },
    { value: "anchor", label: "Anchor Navigation" },
    { value: "sticky-split", label: "Sticky Split" },
    { value: "tiles", label: "Tiles" },
];

const TILES_ITEM_ASPECTS = [
    { value: "auto", label: "Auto" },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "16/9", label: "16:9" },
];

const SS_MAIN_SIDES = [
    { value: "left", label: "Left" },
    { value: "right", label: "Right" },
];

const SS_ITEM_ASPECTS = [
    { value: "auto", label: "Auto" },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "16/9", label: "16:9" },
];

const SS_CLICK_ACTIONS = [
    { value: "lightbox", label: "Lightbox" },
    { value: "none", label: "None" },
];

const HG_ITEM_ASPECTS = [
    { value: "auto", label: "Auto" },
    { value: "1/1", label: "1:1" },
    { value: "4/3", label: "4:3" },
    { value: "3/2", label: "3:2" },
    { value: "16/9", label: "16:9" },
];

// ── General sub-panel ─────────────────────────────────────────────────────────

function GeneralSubPanel({ settings, s, actions, gallerySubTab }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title="Gallery Type"
                description="Choose the overall gallery layout. Thumbnail Gallery shows images alongside a strip of clickable thumbnails. Anchor Navigation stacks all images vertically with dot navigation. Sticky Split shows a sticky main viewer alongside a vertically scrollable column of full-size images."
            >
                <SettingRow
                    label="Gallery Type"
                    description="Sets the primary display mode for the product gallery."
                    className="flex flex-col items-start gap-2 wpxs:flex-row wpxs:items-center wpxs:justify-between"
                >
                    <ButtonGroup
                        options={GALLERY_TYPES}
                        value={settings.galleryType}
                        className="grid grid-cols-2 wpsm:inline-flex wpsm:items-center"
                        onChange={(v) => {
                            s("galleryType", v);
                            if (v === "anchor") {
                                if (
                                    ["top", "bottom"].includes(
                                        settings.thumbnailPosition,
                                    )
                                ) {
                                    s("thumbnailPosition", "left");
                                }
                                // anchor only supports left/right position
                                if (
                                    !["left", "right"].includes(
                                        settings.mainImagePaginationPosition,
                                    )
                                ) {
                                    s("mainImagePaginationPosition", "left");
                                }
                                // anchor dots always require "dots" type
                                if (
                                    settings.mainImagePaginationType !== "dots"
                                ) {
                                    s("mainImagePaginationType", "dots");
                                }
                                if (gallerySubTab === "transition") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v !== "anchor") {
                                // count/progressbar only support top/bottom; reset if leaving anchor with left/right
                                if (
                                    (settings.mainImagePaginationType ===
                                        "count" ||
                                        settings.mainImagePaginationType ===
                                            "progressbar") &&
                                    ["left", "right"].includes(
                                        settings.mainImagePaginationPosition,
                                    )
                                ) {
                                    s("mainImagePaginationPosition", "bottom");
                                }
                            }
                            if (v === "sticky-split") {
                                if (gallerySubTab === "transition") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v === "hierarchy-grid") {
                                if (gallerySubTab === "transition") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v === "tiles") {
                                if (
                                    ["transition", "thumbnails", "main-image", "navigation"].includes(
                                        gallerySubTab,
                                    )
                                ) {
                                    actions.setGallerySubTab("general");
                                }
                            }
                            if (v !== "tiles") {
                                // If switching away from tiles while on tiles sub-tab, go back to general
                                if (gallerySubTab === "tiles") {
                                    actions.setGallerySubTab("general");
                                }
                            }
                        }}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

// ── Thumbnails sub-panel ──────────────────────────────────────────────────────

function ThumbnailsSubPanel({ settings, s }) {
    const showThumbs = settings.thumbnailShow !== false;
    const isSlider = settings.thumbnailLayout === "slider";
    const isGrid = settings.thumbnailLayout === "grid";
    const isSidePos = ["left", "right"].includes(settings.thumbnailPosition);

    return (
        <div className="space-y-5">
            <SectionCard
                title="Thumbnail Image Size"
                description="Choose which WordPress-registered image size is loaded for thumbnail items."
            >
                <SettingRow
                    label="Thumbnail Image"
                    description="Image size used for the thumbnail strip items."
                >
                    <SelectInput
                        options={IMAGE_SIZES}
                        value={settings.thumbnailImageSize}
                        onChange={(v) => s("thumbnailImageSize", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Thumbnails"
                description="Configure gallery thumbnail position and layout."
            >
                <SettingRow
                    label="Show Thumbnails"
                    description="Display the thumbnail strip alongside the main product image."
                >
                    <Toggle
                        value={settings.thumbnailShow !== false}
                        onChange={(v) => s("thumbnailShow", v)}
                    />
                </SettingRow>

                {showThumbs && (
                    <>
                        <SettingRow
                            label="Layout"
                            description="How thumbnails are arranged."
                        >
                            <ButtonGroup
                                options={THUMB_LAYOUTS}
                                value={settings.thumbnailLayout}
                                onChange={(v) => s("thumbnailLayout", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Position"
                            description={
                                settings.galleryType === "anchor"
                                    ? "Where thumbnails appear relative to the stacked images. Only left and right are supported for Anchor Navigation."
                                    : "Where thumbnails appear relative to the main image."
                            }
                        >
                            <ButtonGroup
                                options={
                                    settings.galleryType === "anchor"
                                        ? THUMB_POSITIONS.filter(
                                              (p) =>
                                                  p.value === "left" ||
                                                  p.value === "right",
                                          )
                                        : THUMB_POSITIONS
                                }
                                value={
                                    settings.galleryType === "anchor" &&
                                    settings.thumbnailPosition !== "left" &&
                                    settings.thumbnailPosition !== "right"
                                        ? "left"
                                        : settings.thumbnailPosition
                                }
                                onChange={(v) => s("thumbnailPosition", v)}
                            />
                        </SettingRow>
                        {settings.galleryType !== "anchor" && (
                            <>
                                <SettingRow
                                    label="Alignment"
                                    description="Position of thumbnails within the strip when they don't fill the full area. Has no effect when Fit to Main Image is on or when thumbnails overflow and scroll."
                                >
                                    <ButtonGroup
                                        options={
                                            isSidePos
                                                ? THUMB_ALIGNMENTS_VERTICAL
                                                : THUMB_ALIGNMENTS_HORIZONTAL
                                        }
                                        value={settings.thumbnailAlignment}
                                        onChange={(v) =>
                                            s("thumbnailAlignment", v)
                                        }
                                    />
                                </SettingRow>

                                {isSidePos && (
                                    <SettingRow
                                        label="Responsive Breakpoint"
                                        description="When the gallery width drops below this value, left/right thumbnails move to the bottom. Set to 0 to disable."
                                    >
                                        <NumberInput
                                            value={settings.thumbnailBreakpoint}
                                            onChange={(v) =>
                                                s("thumbnailBreakpoint", v)
                                            }
                                            min={0}
                                            max={2560}
                                            unit="px"
                                            width="w-24"
                                        />
                                    </SettingRow>
                                )}
                            </>
                        )}

                        <SettingRow
                            label="Trigger"
                            description="How hovering or clicking a thumbnail swaps the main image."
                        >
                            <ButtonGroup
                                options={THUMB_TRIGGERS}
                                value={settings.thumbnailTrigger}
                                onChange={(v) => s("thumbnailTrigger", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Entrance Animation"
                            description="Thumbnails fade and slide in with a staggered delay when the gallery first loads."
                        >
                            <Toggle
                                value={settings.thumbnailAnimation}
                                onChange={(v) => s("thumbnailAnimation", v)}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            {showThumbs && (
                <>
                    <SectionCard
                        title="Thumbnail Size"
                        description="Set the dimensions of individual thumbnail images."
                    >
                        <SettingRow
                            label="Width"
                            description="CSS display width of each thumbnail. Does not affect the image file loaded."
                        >
                            <NumberInput
                                value={settings.thumbnailWidth}
                                onChange={(v) => s("thumbnailWidth", v)}
                                min={40}
                                max={300}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Height"
                            description="CSS display height of each thumbnail. Does not affect the image file loaded."
                        >
                            <NumberInput
                                value={settings.thumbnailHeight}
                                onChange={(v) => s("thumbnailHeight", v)}
                                min={40}
                                max={300}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Gap"
                            description="Space between thumbnail items."
                        >
                            <NumberInput
                                value={settings.thumbnailGap}
                                onChange={(v) => s("thumbnailGap", v)}
                                min={0}
                                max={40}
                                unit="px"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Border Radius"
                            description="Rounded corner radius for thumbnails."
                        >
                            <NumberInput
                                value={settings.thumbnailBorderRadius}
                                onChange={(v) => s("thumbnailBorderRadius", v)}
                                min={0}
                                max={50}
                                unit="px"
                            />
                        </SettingRow>

                        {isSidePos && !settings.thumbnailFitToImage && (
                            <InfoBox variant="tip">
                                <strong>Tip:</strong> With a Left/Right
                                position, enabling <em>Fit to Main Image</em> is
                                recommended — it scales thumbnails to fill the
                                full height of the main image for a cleaner
                                look.
                            </InfoBox>
                        )}

                        <SettingRow
                            label="Fit to Main Image"
                            description="Stretch or shrink thumbnails to fill the full width (or height for left/right) of the main image area."
                        >
                            <Toggle
                                value={settings.thumbnailFitToImage}
                                onChange={(v) => s("thumbnailFitToImage", v)}
                            />
                        </SettingRow>
                    </SectionCard>

                    <SectionCard
                        title="Active Thumbnail"
                        description="Styling applied to the currently selected thumbnail."
                    >
                        <SettingRow
                            label="Highlight Color"
                            description="Border color shown on the active thumbnail."
                        >
                            <ColorInput
                                value={settings.thumbnailActiveBorderColor}
                                onChange={(v) =>
                                    s("thumbnailActiveBorderColor", v)
                                }
                            />
                        </SettingRow>

                        <SettingRow
                            label="Highlight Width"
                            description="Thickness of the active thumbnail border."
                        >
                            <NumberInput
                                value={settings.thumbnailActiveBorderWidth}
                                onChange={(v) =>
                                    s("thumbnailActiveBorderWidth", v)
                                }
                                min={1}
                                max={10}
                                unit="px"
                            />
                        </SettingRow>
                    </SectionCard>

                    {isSlider && (
                        <SectionCard
                            title="Slider Options"
                            description="Settings specific to the slider layout."
                        >
                            <SettingRow
                                label="Navigation Arrows"
                                description="Show previous/next arrow buttons on the thumbnail slider."
                            >
                                <Toggle
                                    value={settings.thumbnailSliderButtons}
                                    onChange={(v) =>
                                        s("thumbnailSliderButtons", v)
                                    }
                                />
                            </SettingRow>

                            {settings.thumbnailSliderButtons && (
                                <SettingRow
                                    label="Overlay Arrows"
                                    description="Position arrows over the slider edges so thumbnails fill the full strip width."
                                >
                                    <Toggle
                                        value={settings.thumbnailArrowsOverlay}
                                        onChange={(v) =>
                                            s("thumbnailArrowsOverlay", v)
                                        }
                                    />
                                </SettingRow>
                            )}

                            <SettingRow
                                label="Slide Type"
                                description="Slide by Row moves a full visible page at once. Slide by Item moves one thumbnail at a time."
                            >
                                <ButtonGroup
                                    options={THUMB_SLIDE_TYPES}
                                    value={settings.thumbnailSlideType}
                                    onChange={(v) => s("thumbnailSlideType", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label="Loop"
                                description="Allow the slider to wrap around from the last thumbnail back to the first, and vice versa. The wrap-around uses a distinct fade animation."
                            >
                                <Toggle
                                    value={settings.thumbnailLoop}
                                    onChange={(v) => s("thumbnailLoop", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label="Drag Navigation"
                                description="Click-and-drag or touch-drag to scroll through thumbnails."
                            >
                                <Toggle
                                    value={settings.thumbnailDragNav !== false}
                                    onChange={(v) => s("thumbnailDragNav", v)}
                                />
                            </SettingRow>

                            <SettingRow
                                label="Scroll Navigation"
                                description="Use the mouse wheel or trackpad to scroll through thumbnails. Scrolls vertically for left/right strips and horizontally for top/bottom strips."
                            >
                                <Toggle
                                    value={
                                        settings.thumbnailScrollNav !== false
                                    }
                                    onChange={(v) => s("thumbnailScrollNav", v)}
                                />
                            </SettingRow>
                        </SectionCard>
                    )}

                    {isGrid && settings.galleryType !== "anchor" && (
                        <SectionCard
                            title="Grid Options"
                            description="Settings specific to the grid layout."
                        >
                            <SettingRow
                                label="Columns"
                                description="Number of columns in the thumbnail grid."
                            >
                                <NumberInput
                                    value={settings.thumbnailGridColumns}
                                    onChange={(v) =>
                                        s("thumbnailGridColumns", v)
                                    }
                                    min={1}
                                    max={8}
                                />
                            </SettingRow>
                        </SectionCard>
                    )}
                </>
            )}
        </div>
    );
}

// ── Main Image sub-panel ──────────────────────────────────────────────────────

function MainImageSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title="Image File Size"
                description="Choose which WordPress-registered image size is served for the main product image. Larger sizes are sharper but slower."
            >
                <SettingRow
                    label="Main Image"
                    description="Image size used for the main product image display."
                >
                    <SelectInput
                        options={IMAGE_SIZES}
                        value={settings.mainImageSize}
                        onChange={(v) => s("mainImageSize", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Main Image"
                description="Control the main product image display area."
            >
                {settings.galleryType !== "anchor" && (
                    <SettingRow
                        label="Height Mode"
                        description="Lock freezes the height after the first item loads — prevents layout shift. Adaptive grows the container to fit the tallest item seen, never shrinking. Fixed sets an explicit pixel height."
                    >
                        <ButtonGroup
                            options={HEIGHT_MODES}
                            value={settings.mainImageHeightMode}
                            onChange={(v) => s("mainImageHeightMode", v)}
                        />
                    </SettingRow>
                )}

                {settings.mainImageHeightMode === "fixed" && (
                    <SettingRow
                        label="Fixed Height"
                        description="Explicit height for the main image area in pixels."
                    >
                        <NumberInput
                            value={settings.mainImageHeight || 0}
                            onChange={(v) => s("mainImageHeight", v || 0)}
                            min={100}
                            max={1200}
                            unit="px"
                            width="w-24"
                        />
                    </SettingRow>
                )}

                {settings.galleryType !== "anchor" && (
                    <SettingRow
                        label="Image Fit"
                        description="How the image fills the display area."
                    >
                        <ButtonGroup
                            options={IMG_FITS}
                            value={settings.mainImageFit}
                            onChange={(v) => s("mainImageFit", v)}
                        />
                    </SettingRow>
                )}

                <SettingRow
                    label="Background Color"
                    description="Background shown behind the image."
                >
                    <ColorInput
                        value={settings.mainImageBackground}
                        onChange={(v) => s("mainImageBackground", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Strip Gap"
                    description="Space between the thumbnail strip and the main image area. Independent of the gap between individual thumbnail items."
                >
                    <NumberInput
                        value={settings.thumbnailStripGap}
                        onChange={(v) => s("thumbnailStripGap", v)}
                        min={0}
                        max={80}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Border Radius"
                    description="Rounded corner radius of the main image and its container."
                >
                    <NumberInput
                        value={settings.mainImageBorderRadius}
                        onChange={(v) => s("mainImageBorderRadius", v)}
                        min={0}
                        max={80}
                        unit="px"
                    />
                </SettingRow>

                <SettingRow
                    label="Border Width"
                    description="Thickness of the border around the main image area."
                >
                    <NumberInput
                        value={settings.mainImageBorderWidth}
                        onChange={(v) => s("mainImageBorderWidth", v)}
                        min={0}
                        max={10}
                        unit="px"
                    />
                </SettingRow>

                {settings.mainImageBorderWidth > 0 && (
                    <SettingRow label="Border Color">
                        <ColorInput
                            value={settings.mainImageBorderColor}
                            onChange={(v) => s("mainImageBorderColor", v)}
                        />
                    </SettingRow>
                )}

                <SettingRow
                    label="Drop Shadow"
                    description="Apply a soft shadow around the main image area."
                >
                    <Toggle
                        value={settings.mainImageShadow}
                        onChange={(v) => s("mainImageShadow", v)}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

// ── Navigation sub-panel ─────────────────────────────────────────────────────

function NavigationSubPanel({ settings, s, actions }) {
    // Write back a corrected position whenever galleryType or pagination type
    // makes the stored mainImagePaginationPosition invalid (e.g. "bottom" is
    // invalid for anchor; "left"/"right" are invalid for count/progressbar).
    useEffect(() => {
        if (!settings.mainImagePagination) return;
        const isTopBottomOnly =
            settings.mainImagePaginationType === "count" ||
            settings.mainImagePaginationType === "progressbar";
        const posOpts =
            settings.galleryType === "anchor"
                ? PAGINATION_POSITIONS.filter(
                      (p) => p.value === "left" || p.value === "right",
                  )
                : isTopBottomOnly
                ? PAGINATION_POSITIONS.filter(
                      (p) => p.value === "bottom" || p.value === "top",
                  )
                : PAGINATION_POSITIONS;
        const isValid = posOpts.some(
            (p) => p.value === settings.mainImagePaginationPosition,
        );
        if (!isValid && posOpts.length > 0) {
            s("mainImagePaginationPosition", posOpts[0].value);
        }
    }, [
        settings.galleryType,
        settings.mainImagePaginationType,
        settings.mainImagePagination,
        settings.mainImagePaginationPosition,
    ]);

    return (
        <div className="space-y-5">
            <SectionCard
                title="Main Image Navigation"
                description="Arrows, pagination, and lightbox button controls for the main product image."
            >
                {settings.galleryType === "sticky-split" &&
                    (settings.mainImageArrows ||
                        settings.mainImagePagination) && (
                        <InfoBox variant="tip">
                            With <strong>Sticky Split</strong>, the scroll
                            column acts as the primary navigation. It is
                            recommended to keep{" "}
                            <strong>Navigation Arrows</strong> and{" "}
                            <strong>Pagination</strong> disabled.
                        </InfoBox>
                    )}

                {settings.galleryType !== "anchor" && (
                    <>
                        <SettingRow
                            label="Navigation Arrows"
                            description="Show previous/next arrow buttons on the main image. Syncs with the thumbnail strip."
                        >
                            <Toggle
                                value={settings.mainImageArrows}
                                onChange={(v) => s("mainImageArrows", v)}
                            />
                        </SettingRow>

                        {settings.mainImageArrows && (
                            <SettingRow
                                label="Auto-hide Arrows"
                                description="Hide arrows when the cursor leaves the image. Turn off to keep them always visible."
                            >
                                <Toggle
                                    value={settings.mainImageArrowsAutoHide}
                                    onChange={(v) =>
                                        s("mainImageArrowsAutoHide", v)
                                    }
                                />
                            </SettingRow>
                        )}

                        <SettingRow
                            label="Pagination"
                            description="Show a pagination indicator dots for the main image for quick navigation."
                        >
                            <Toggle
                                value={settings.mainImagePagination}
                                onChange={(v) => s("mainImagePagination", v)}
                            />
                        </SettingRow>

                        {settings.mainImagePagination && (
                            <SettingRow
                                label="Pagination Style"
                                description="Dots are clickable indicators. Progress Bar shows a thin fill line that advances with each image. Count shows a numeric counter (e.g. 2 / 8) with arrow buttons to navigate."
                            >
                                <ButtonGroup
                                    options={PAGINATION_TYPES}
                                    value={settings.mainImagePaginationType}
                                    onChange={(v) => {
                                        s("mainImagePaginationType", v);
                                        if (
                                            (v === "count" ||
                                                v === "progressbar") &&
                                            ["left", "right"].includes(
                                                settings.mainImagePaginationPosition,
                                            )
                                        ) {
                                            s(
                                                "mainImagePaginationPosition",
                                                "bottom",
                                            );
                                        }
                                    }}
                                />
                            </SettingRow>
                        )}
                    </>
                )}

                {/* Anchor nav: dot position is always relevant — the frontend
                    renders the dot rail unconditionally, so show this control
                    regardless of the mainImagePagination toggle. */}
                {settings.galleryType === "anchor" && (
                    <SettingRow
                        label="Dot Position"
                        description="Which side the scroll-linked navigation dots appear on."
                    >
                        <ButtonGroup
                            options={PAGINATION_POSITIONS.filter(
                                (p) =>
                                    p.value === "left" || p.value === "right",
                            )}
                            value={
                                ["left", "right"].includes(
                                    settings.mainImagePaginationPosition,
                                )
                                    ? settings.mainImagePaginationPosition
                                    : "left"
                            }
                            onChange={(v) =>
                                s("mainImagePaginationPosition", v)
                            }
                        />
                    </SettingRow>
                )}

                {/* Non-anchor: position only matters when pagination is on. */}
                {settings.mainImagePagination &&
                    settings.galleryType !== "anchor" &&
                    (() => {
                        const isTopBottomOnly =
                            settings.mainImagePaginationType === "count" ||
                            settings.mainImagePaginationType === "progressbar";
                        const posOpts = isTopBottomOnly
                            ? PAGINATION_POSITIONS.filter(
                                  (p) =>
                                      p.value === "bottom" || p.value === "top",
                              )
                            : PAGINATION_POSITIONS;
                        const safePos = posOpts.some(
                            (p) =>
                                p.value ===
                                settings.mainImagePaginationPosition,
                        )
                            ? settings.mainImagePaginationPosition
                            : posOpts[0]?.value;
                        return (
                            <SettingRow
                                label="Pagination Position"
                                description="Where the pagination appears. Progress Bar and Count support Top or Bottom only."
                            >
                                <ButtonGroup
                                    options={posOpts}
                                    value={safePos}
                                    onChange={(v) =>
                                        s("mainImagePaginationPosition", v)
                                    }
                                />
                            </SettingRow>
                        );
                    })()}

                {settings.mainImagePagination &&
                    settings.galleryType !== "anchor" && (
                        <SettingRow
                            label="Auto-hide"
                            description="Hide the pagination when the cursor leaves the image. Turn off to keep it always visible."
                        >
                            <Toggle
                                value={settings.mainImagePaginationAutoHide}
                                onChange={(v) =>
                                    s("mainImagePaginationAutoHide", v)
                                }
                            />
                        </SettingRow>
                    )}

                {(settings.galleryType === "thumbnail" ||
                    settings.galleryType === "sticky-split") &&
                    !settings.zoomOnHover &&
                    !settings.touchZoom && (
                        <SettingRow
                            label="Swipe / Drag Navigation"
                            description="Drag or swipe the main image to navigate between gallery items. Works on both desktop (mouse drag) and mobile (touch swipe). Only available when Zoom on Hover and Touch Zoom are both disabled."
                        >
                            <Toggle
                                value={settings.mainImageSwipe}
                                onChange={(v) => s("mainImageSwipe", v)}
                            />
                        </SettingRow>
                    )}

                {settings.galleryType === "thumbnail" && (
                    <SettingRow
                        label="Scroll Navigation"
                        description="Use the mouse scroll wheel to navigate between gallery images. Scrolling in either direction moves to the next or previous image."
                    >
                        <Toggle
                            value={settings.mainImageScrollNav}
                            onChange={(v) => s("mainImageScrollNav", v)}
                        />
                    </SettingRow>
                )}

                {settings.galleryType === "thumbnail" &&
                    settings.mainImageScrollNav &&
                    settings.zoomOnHover && (
                        <InfoBox variant="tip">
                            <strong>Note:</strong> Scroll Navigation works best
                            when{" "}
                            <strong>
                                <button
                                    type="button"
                                    onClick={() => actions.setTab("zoom")}
                                >
                                    Zoom on Hover
                                </button>
                            </strong>{" "}
                            is disabled — enabling both features at the same
                            time could cause interference.
                        </InfoBox>
                    )}

                {settings.galleryType === "thumbnail" &&
                    settings.mainImageScrollNav && (
                        <SettingRow
                            label="Scroll Response Speed"
                            description="Minimum time between scroll-triggered navigations. Higher values slow down how fast scrolling moves through images."
                        >
                            <NumberInput
                                value={settings.mainImageScrollNavDelay ?? 500}
                                onChange={(v) =>
                                    s("mainImageScrollNavDelay", v)
                                }
                                min={100}
                                max={2000}
                                unit="ms"
                                width="w-24"
                            />
                        </SettingRow>
                    )}

                {settings.lightbox && settings.lightboxOnClick && (
                    <SettingRow
                        label="Auto-hide Expand Button"
                        description="Hide the lightbox expand button when the cursor leaves the image. Turn off to keep it always visible."
                    >
                        <Toggle
                            value={settings.lightboxButtonAutoHide}
                            onChange={(v) => s("lightboxButtonAutoHide", v)}
                        />
                    </SettingRow>
                )}
            </SectionCard>
        </div>
    );
}

// ── Transition sub-panel ─────────────────────────────────────────────────────

function TransitionSubPanel({ settings, s }) {
    const isSidePos = ["left", "right"].includes(settings.thumbnailPosition);

    return (
        <div className="space-y-5">
            <SectionCard
                title="Image Transition"
                description="Animation when switching between gallery images."
            >
                <SettingRow label="Effect">
                    <ButtonGroup
                        options={TRANSITIONS}
                        value={settings.mainImageTransition}
                        onChange={(v) => s("mainImageTransition", v)}
                    />
                </SettingRow>

                {settings.mainImageTransition === "slide" && (
                    <>
                        <SettingRow
                            label="Slide Direction"
                            description="Direction images slide when navigating between gallery items."
                        >
                            <ButtonGroup
                                options={SLIDE_DIRECTIONS}
                                value={settings.mainImageSlideDirection}
                                onChange={(v) =>
                                    s("mainImageSlideDirection", v)
                                }
                            />
                        </SettingRow>

                        {isSidePos &&
                            settings.mainImageSlideDirection !== "vertical" && (
                                <InfoBox>
                                    <strong>Tip:</strong> With a Left/Right
                                    thumbnail position, setting the slide
                                    direction to <em>Vertical</em> complements
                                    the side-by-side layout better.
                                </InfoBox>
                            )}

                        {!isSidePos &&
                            settings.mainImageSlideDirection !==
                                "horizontal" && (
                                <InfoBox>
                                    <strong>Tip:</strong> With a Top/Bottom
                                    thumbnail position, setting the slide
                                    direction to <em>Horizontal</em> complements
                                    the stacked layout better.
                                </InfoBox>
                            )}
                    </>
                )}

                {settings.mainImageTransition !== "none" && (
                    <SettingRow
                        label="Duration"
                        description="How long the transition animation takes."
                    >
                        <NumberInput
                            value={settings.mainImageTransitionDuration}
                            onChange={(v) =>
                                s("mainImageTransitionDuration", v)
                            }
                            min={50}
                            max={2000}
                            unit="ms"
                            width="w-24"
                        />
                    </SettingRow>
                )}
            </SectionCard>
        </div>
    );
}

// ── Stacked Layout sub-panel ──────────────────────────────────────────────────

function StackedSubPanel({ settings, s, actions }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title="Stacked Layout"
                description="Customise the Anchor Navigation (stacked) gallery layout and its dot pagination."
            >
                <SettingRow
                    label="Item Gap"
                    description="Vertical space between each stacked gallery item."
                >
                    <NumberInput
                        value={settings.stackedItemGap}
                        onChange={(v) => s("stackedItemGap", v)}
                        min={0}
                        max={120}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Navigation Type"
                    description="Navigation type for the Stacked Layout. Dot Navigation places clickable dots on the side for navigation. Thumbnails shows a vertical strip of thumbnail images instead."
                >
                    <ButtonGroup
                        options={[
                            { value: "dots", label: "Dot" },
                            { value: "thumbs", label: "Thumbnail" },
                        ]}
                        value={settings.stackedNavigationType}
                        onChange={(v) => s("stackedNavigationType", v)}
                    />
                </SettingRow>

                {settings.stackedNavigationType === "thumbs" && (
                    <SettingRow
                        label="Sticky Offset"
                        description="Distance from the top of the viewport when the thumbnail strip is sticky (left/right positions only)."
                    >
                        <NumberInput
                            value={settings.stackedThumbsStickyTop}
                            onChange={(v) => s("stackedThumbsStickyTop", v)}
                            min={0}
                            max={500}
                            unit="px"
                            width="w-24"
                        />
                    </SettingRow>
                )}

                {settings.stackedNavigationType === "thumbs" && (
                    <InfoBox className="mt-2 mb-2">
                        Go to the{" "}
                        <button
                            type="button"
                            onClick={() =>
                                actions.setGallerySubTab("thumbnails")
                            }
                            className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                        >
                            Thumbnails tab
                        </button>{" "}
                        and adjust the thumbnail settings there to style the
                        thumbnail navigation for the stacked layout.
                    </InfoBox>
                )}

                {settings.stackedNavigationType === "dots" && (
                    <>
                        <SettingRow
                            label="Dot Navigation"
                            description="Overlay dots float on top of the images. Outside places them in a separate column beside the gallery."
                        >
                            <ButtonGroup
                                options={[
                                    { value: "overlay", label: "Overlay" },
                                    { value: "outside", label: "Outside" },
                                ]}
                                value={settings.stackedDotsPosition}
                                onChange={(v) => s("stackedDotsPosition", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Dot Gap"
                            description="Space between the pagination dots."
                        >
                            <NumberInput
                                value={settings.stackedDotGap}
                                onChange={(v) => s("stackedDotGap", v)}
                                min={2}
                                max={30}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Dot Size"
                            description="Diameter of each pagination dot."
                        >
                            <NumberInput
                                value={settings.stackedDotSize}
                                onChange={(v) => s("stackedDotSize", v)}
                                min={4}
                                max={24}
                                unit="px"
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Dot Color"
                            description="Color of the inactive pagination dots."
                        >
                            <ColorInput
                                value={settings.stackedDotColor}
                                onChange={(v) => s("stackedDotColor", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Active Dot Color"
                            description="Color of the currently active pagination dot."
                        >
                            <ColorInput
                                value={settings.stackedDotActiveColor}
                                onChange={(v) => s("stackedDotActiveColor", v)}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title="Zoom"
                description="Hover zoom for the stacked images. Uses the zoom type and settings from the Zoom tab."
            >
                <SettingRow
                    label="Zoom on Hover"
                    description="Enable zoom when hovering over each image in the stacked gallery. Requires Zoom on Hover to be enabled in the Zoom tab."
                >
                    <Toggle
                        value={settings.anchorZoom !== false}
                        onChange={(v) => s("anchorZoom", v)}
                    />
                </SettingRow>

                {settings.anchorZoom !== false && !settings.zoomOnHover && (
                    <InfoBox variant="tip">
                        <strong>Zoom on Hover</strong> is currently disabled in
                        the Zoom tab. Enable it there for this setting to take
                        effect.
                    </InfoBox>
                )}
            </SectionCard>
        </div>
    );
}

// ── Hierarchy Grid sub-panel ──────────────────────────────────────────────────

function HierarchyGridSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title="Grid Layout"
                description="Configure the hierarchy grid below the hero image. The first gallery image is always displayed full-width; remaining images fill a multi-column grid."
            >
                <SettingRow
                    label="Grid Columns"
                    description="Number of columns in the grid below the hero image."
                >
                    <NumberInput
                        value={settings.hierarchyGridColumns}
                        onChange={(v) => s("hierarchyGridColumns", v)}
                        min={1}
                        max={6}
                        width="w-20"
                    />
                </SettingRow>

                <SettingRow
                    label="Gap"
                    description="Space between the hero image and grid items, and between grid items."
                >
                    <NumberInput
                        value={settings.hierarchyGridGap}
                        onChange={(v) => s("hierarchyGridGap", v)}
                        min={0}
                        max={80}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Item Border Radius"
                    description="Rounded corner radius applied to each grid item."
                >
                    <NumberInput
                        value={settings.hierarchyGridItemBorderRadius}
                        onChange={(v) => s("hierarchyGridItemBorderRadius", v)}
                        min={0}
                        max={60}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Grid Item Style"
                description="Control how images inside the grid cells are sized and fitted."
            >
                <SettingRow
                    label="Aspect Ratio"
                    description="Enforce a fixed aspect ratio on grid items. Auto uses each image's natural dimensions."
                >
                    <ButtonGroup
                        options={HG_ITEM_ASPECTS}
                        value={settings.hierarchyGridItemAspect}
                        onChange={(v) => s("hierarchyGridItemAspect", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Image Fit"
                    description="How images fill the grid item area when an aspect ratio is enforced."
                >
                    <ButtonGroup
                        options={IMG_FITS}
                        value={settings.hierarchyGridItemFit}
                        onChange={(v) => s("hierarchyGridItemFit", v)}
                    />
                </SettingRow>
            </SectionCard>

            {settings.zoomOnHover !== false && (
                <SectionCard
                    title="Zoom"
                    description="Hover zoom for individual images in the hierarchy grid. Uses the zoom type and settings from the Zoom tab."
                >
                    <SettingRow
                        label="Zoom on Hover"
                        description="Enable zoom when hovering over each image in the grid. Requires Zoom on Hover to be enabled in the Zoom tab."
                    >
                        <Toggle
                            value={settings.hierarchyGridZoom !== false}
                            onChange={(v) => s("hierarchyGridZoom", v)}
                        />
                    </SettingRow>

                    {settings.hierarchyGridZoom !== false &&
                        !settings.zoomOnHover && (
                            <InfoBox variant="tip">
                                <strong>Zoom on Hover</strong> is currently
                                disabled in the Zoom tab. Enable it there for
                                this setting to take effect.
                            </InfoBox>
                        )}
                </SectionCard>
            )}
        </div>
    );
}

// ── Sticky Split sub-panel ────────────────────────────────────────────────────

function StickySplitSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title="Layout"
                description="Configure the two-column sticky split layout. The sticky column holds the main image viewer; the scroll column shows all images full-size."
            >
                <SettingRow
                    label="Main Side"
                    description="Which column holds the sticky image viewer."
                >
                    <ButtonGroup
                        options={SS_MAIN_SIDES}
                        value={settings.stickySplitMainSide}
                        onChange={(v) => s("stickySplitMainSide", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Main Column Width"
                    description="Percentage of the total gallery width used by the sticky viewer column."
                >
                    <NumberInput
                        value={settings.stickySplitMainWidthPercent}
                        onChange={(v) => s("stickySplitMainWidthPercent", v)}
                        min={20}
                        max={80}
                        unit="%"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Column Gap"
                    description="Space between the sticky viewer column and the scroll column."
                >
                    <NumberInput
                        value={settings.stickySplitGap}
                        onChange={(v) => s("stickySplitGap", v)}
                        min={0}
                        max={120}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Sticky Top Offset"
                    description="Distance from the top of the viewport when the main viewer is in sticky position."
                >
                    <NumberInput
                        value={settings.stickySplitStickyTop}
                        onChange={(v) => s("stickySplitStickyTop", v)}
                        min={0}
                        max={300}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Click Sync"
                    description="When enabled, clicking a scroll item also swaps the sticky viewer to that image. Independent of the lightbox click action."
                >
                    <Toggle
                        value={settings.stickySplitClickSync}
                        onChange={(v) => s("stickySplitClickSync", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Scroll Column"
                description="Style the full-size images in the scrollable column."
            >
                <SettingRow
                    label="Item Gap"
                    description="Vertical space between images in the scroll column."
                >
                    <NumberInput
                        value={settings.stickySplitScrollItemGap}
                        onChange={(v) => s("stickySplitScrollItemGap", v)}
                        min={0}
                        max={120}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Aspect Ratio"
                    description="Enforce a fixed aspect ratio on scroll items. Auto uses each image's natural ratio."
                >
                    <ButtonGroup
                        options={SS_ITEM_ASPECTS}
                        value={settings.stickySplitScrollItemAspect}
                        onChange={(v) => s("stickySplitScrollItemAspect", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Image Fit"
                    description="How images fill the scroll item area when an aspect ratio is enforced."
                >
                    <ButtonGroup
                        options={IMG_FITS}
                        value={settings.stickySplitScrollItemFit}
                        onChange={(v) => s("stickySplitScrollItemFit", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Border Radius"
                    description="Rounded corner radius applied to each scroll item."
                >
                    <NumberInput
                        value={settings.stickySplitScrollItemBorderRadius}
                        onChange={(v) =>
                            s("stickySplitScrollItemBorderRadius", v)
                        }
                        min={0}
                        max={60}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Show Captions"
                    description="Display caption text below each scroll item."
                >
                    <Toggle
                        value={settings.stickySplitShowCaption}
                        onChange={(v) => s("stickySplitShowCaption", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Zoom on Hover"
                    description="Enable zoom when hovering over images in the scroll column. Uses the same zoom type configured for the main viewer."
                >
                    <Toggle
                        value={settings.stickySplitThumbZoom}
                        onChange={(v) => s("stickySplitThumbZoom", v)}
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Click Action"
                description="What happens when a scroll item is clicked."
            >
                <SettingRow
                    label="On Click"
                    description="Open the lightbox viewer, or disable click interaction."
                >
                    <ButtonGroup
                        options={SS_CLICK_ACTIONS}
                        value={settings.stickySplitClickAction}
                        onChange={(v) => s("stickySplitClickAction", v)}
                    />
                </SettingRow>
            </SectionCard>
        </div>
    );
}

// ── Tiles sub-panel ───────────────────────────────────────────────────────────

function TilesSubPanel({ settings, s }) {
    return (
        <div className="space-y-5">
            <SectionCard
                title="Tiles Grid"
                description="All product images are displayed as equal-sized tiles in a grid. A Show More / Show Less button controls how many tiles are visible at once."
            >
                <SettingRow
                    label="Columns"
                    description="Number of equal-width columns in the tiles grid."
                >
                    <NumberInput
                        value={settings.tilesColumns}
                        onChange={(v) => s("tilesColumns", v)}
                        min={1}
                        max={6}
                        width="w-20"
                    />
                </SettingRow>

                <SettingRow
                    label="Gap"
                    description="Space between tiles."
                >
                    <NumberInput
                        value={settings.tilesGap}
                        onChange={(v) => s("tilesGap", v)}
                        min={0}
                        max={80}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>

                <SettingRow
                    label="Aspect Ratio"
                    description="Enforce a fixed aspect ratio on each tile. Auto uses each image's natural dimensions."
                >
                    <ButtonGroup
                        options={TILES_ITEM_ASPECTS}
                        value={settings.tilesItemAspect}
                        onChange={(v) => s("tilesItemAspect", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Image Fit"
                    description="How images fill the tile area when an aspect ratio is enforced."
                >
                    <ButtonGroup
                        options={IMG_FITS}
                        value={settings.tilesItemFit}
                        onChange={(v) => s("tilesItemFit", v)}
                    />
                </SettingRow>

                <SettingRow
                    label="Border Radius"
                    description="Rounded corner radius applied to each tile."
                >
                    <NumberInput
                        value={settings.tilesItemBorderRadius}
                        onChange={(v) => s("tilesItemBorderRadius", v)}
                        min={0}
                        max={60}
                        unit="px"
                        width="w-24"
                    />
                </SettingRow>
            </SectionCard>

            <SectionCard
                title="Show More / Show Less"
                description="Limit the initial number of visible tiles and let visitors reveal the rest with a button."
            >
                <SettingRow
                    label="Show More Button"
                    description="Display a button at the bottom that reveals tiles beyond the initial count."
                >
                    <Toggle
                        value={settings.tilesShowMoreEnabled !== false}
                        onChange={(v) => s("tilesShowMoreEnabled", v)}
                    />
                </SettingRow>

                {settings.tilesShowMoreEnabled !== false && (
                    <>
                        <SettingRow
                            label="Initial Count"
                            description="Number of tiles visible before the Show More button is clicked. Set to 0 to show all tiles."
                        >
                            <NumberInput
                                value={settings.tilesInitialCount}
                                onChange={(v) => s("tilesInitialCount", v)}
                                min={0}
                                max={100}
                                width="w-24"
                            />
                        </SettingRow>

                        <SettingRow
                            label="Show More Text"
                            description="Label on the button that reveals hidden tiles."
                        >
                            <TextInput
                                value={settings.tilesShowMoreText}
                                onChange={(v) => s("tilesShowMoreText", v)}
                            />
                        </SettingRow>

                        <SettingRow
                            label="Show Less Text"
                            description="Label on the button that collapses tiles back to the initial count."
                        >
                            <TextInput
                                value={settings.tilesShowLessText}
                                onChange={(v) => s("tilesShowLessText", v)}
                            />
                        </SettingRow>
                    </>
                )}
            </SectionCard>

            <SectionCard
                title="Zoom"
                description="Hover zoom for individual tiles. Uses the zoom type and settings from the Zoom tab."
            >
                <SettingRow
                    label="Zoom on Hover"
                    description="Enable zoom when hovering over each tile. Requires Zoom on Hover to be enabled in the Zoom tab."
                >
                    <Toggle
                        value={settings.tilesZoom !== false}
                        onChange={(v) => s("tilesZoom", v)}
                    />
                </SettingRow>

                {settings.tilesZoom !== false && !settings.zoomOnHover && (
                    <InfoBox variant="tip">
                        <strong>Zoom on Hover</strong> is currently disabled in
                        the Zoom tab. Enable it there for this setting to take
                        effect.
                    </InfoBox>
                )}
            </SectionCard>
        </div>
    );
}

// ── Root panel — routes to the active sub-panel ───────────────────────────────

export default function GalleryPanel() {
    const {
        state: { settings, gallerySubTab },
        actions,
    } = useSettings();
    const s = (key, val) => actions.set(key, val);

    switch (gallerySubTab) {
        case "general":
            return (
                <GeneralSubPanel
                    settings={settings}
                    s={s}
                    actions={actions}
                    gallerySubTab={gallerySubTab}
                />
            );
        case "main-image":
            return <MainImageSubPanel settings={settings} s={s} />;
        case "navigation":
            return (
                <NavigationSubPanel
                    settings={settings}
                    actions={actions}
                    s={s}
                />
            );
        case "transition":
            if (
                settings.galleryType === "anchor" ||
                settings.galleryType === "sticky-split" ||
                settings.galleryType === "hierarchy-grid" ||
                settings.galleryType === "tiles"
            ) {
                return (
                    <GeneralSubPanel
                        settings={settings}
                        s={s}
                        actions={actions}
                        gallerySubTab={gallerySubTab}
                    />
                );
            }
            return <TransitionSubPanel settings={settings} s={s} />;
        case "stacked":
            return (
                <StackedSubPanel settings={settings} s={s} actions={actions} />
            );
        case "sticky-split":
            return <StickySplitSubPanel settings={settings} s={s} />;
        case "hierarchy-grid":
            return <HierarchyGridSubPanel settings={settings} s={s} />;
        case "tiles":
            return <TilesSubPanel settings={settings} s={s} />;
        default:
            return <ThumbnailsSubPanel settings={settings} s={s} />;
    }
}
